import { Link } from "react-router-dom";
import { YodiMark } from "@/components/brand/YodiMark";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Icon } from "@/lib/icons";
import { cn } from "@/lib/utils";
import type { ShellNavItem, ShellNavSection } from "./types";

interface ShellSidebarProps {
  title: string;
  homeHref: string;
  sections: ShellNavSection[];
  activeKey?: string;
  collapsed?: boolean;
  onNavigate?: () => void;
  onToggleCollapsed?: () => void;
  collapseLabel: string;
  expandLabel: string;
}

/**
 * The menu of every back-office space. Flat titled sections, one level, no
 * accordion: a page that needs sub-views shows them as tabs in the page.
 */
export function ShellSidebar({
  title,
  homeHref,
  sections,
  activeKey,
  collapsed = false,
  onNavigate,
  onToggleCollapsed,
  collapseLabel,
  expandLabel,
}: ShellSidebarProps) {
  return (
    <nav aria-label={title} className="flex h-full w-full flex-col bg-surface">
      <Link
        to={homeHref}
        onClick={onNavigate}
        className={cn("flex h-14 shrink-0 items-center gap-3 border-b border-border/60", collapsed ? "justify-center px-2" : "px-4")}
      >
        <YodiMark className="size-8 rounded-lg text-sm" />
        {!collapsed ? (
          <span className="min-w-0 leading-tight">
            <span className="block truncate text-sm font-semibold text-foreground">{title}</span>
            <span className="block truncate text-[11px] text-faint-foreground">Yodi Data Hub</span>
          </span>
        ) : null}
      </Link>

      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-2 py-4">
        {sections.map((section) => (
          <div key={section.key}>
            {section.title ? (
              collapsed ? (
                <div className="mx-2 mb-2 h-px bg-border/60" aria-hidden="true" />
              ) : (
                <p className="mb-1.5 px-2.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-faint-foreground">{section.title}</p>
              )
            ) : null}
            <ul className="space-y-0.5">
              {section.items.map((item) => (
                <li key={item.key}>
                  <NavLink item={item} active={item.key === activeKey} collapsed={collapsed} onNavigate={onNavigate} />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {onToggleCollapsed ? (
        <div className="shrink-0 border-t border-border/60 p-2">
          <button
            type="button"
            onClick={onToggleCollapsed}
            className={cn(
              "flex h-9 w-full items-center gap-2 rounded-md px-2.5 text-[13px] text-muted-foreground transition-colors hover:bg-surface-2 hover:text-foreground",
              collapsed && "justify-center px-0",
            )}
            aria-label={collapsed ? expandLabel : collapseLabel}
          >
            <Icon name="panelLeft" className="size-4" />
            {!collapsed ? (
              <>
                <span className="flex-1 text-left">{collapseLabel}</span>
                <kbd className="text-[11px] text-faint-foreground">⌘B</kbd>
              </>
            ) : null}
          </button>
        </div>
      ) : null}
    </nav>
  );
}

function NavLink({ item, active, collapsed, onNavigate }: { item: ShellNavItem; active: boolean; collapsed: boolean; onNavigate?: () => void }) {
  const badge = item.badge && item.badge > 0 ? (item.badge > 99 ? "99+" : String(item.badge)) : null;
  const link = (
    <Link
      to={item.href}
      onClick={onNavigate}
      aria-current={active ? "page" : undefined}
      className={cn(
        "relative flex h-9 items-center gap-2.5 rounded-md px-2.5 text-[13px] transition-colors",
        active
          ? "bg-background font-medium text-foreground ring-1 ring-border/70"
          : "text-muted-foreground hover:bg-surface-2 hover:text-foreground",
        collapsed && "justify-center px-0",
      )}
    >
      <Icon name={item.icon} className={cn("size-4 shrink-0", active && "text-brand")} />
      {!collapsed ? <span className="min-w-0 flex-1 truncate">{item.label}</span> : null}
      {badge && !collapsed ? (
        <span className="rounded-full bg-brand/15 px-1.5 text-[11px] font-semibold tabular-nums text-brand">
          {badge}
          <span className="sr-only"> en attente</span>
        </span>
      ) : null}
      {badge && collapsed ? <span className="absolute right-1.5 top-1.5 size-1.5 rounded-full bg-brand" aria-hidden="true" /> : null}
    </Link>
  );
  if (!collapsed) return link;
  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right">{item.label}</TooltipContent>
    </Tooltip>
  );
}
